import { ChangeEvent, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField } from '@mui/material';
import {
  CoreBox,
  CoreDialog,
  CoreDialogContent,
  CoreFormControl,
  CoreFormGroup,
  CoreCheckbox,
  CoreFormControlLabel,
  CoreFormHelperText,
  CoreTypography,
} from '@youscience/core';
import { Roles, RoleEnum } from '@youscience/commonapp-service-common';

import { InputMask } from '@components/Form/InputMask';
import DialogFooter from '@components/DialogFooter';

import { HELPER_TEXT } from '@constants/optIn';
import { ECP_APPS_URL } from '@constants/externalRoutes';

import { useUserProfile } from '@hooks/useUserProfile';

import { RecommenderInfo } from '@interfaces/recommenderWorkspace';

import useCommonAppAdminStore from '@store/CommonAppAdminStore';

import { validateEmail } from '@utils/form';

import { sxStyles, StyledDialogTitle } from './ApplicationRequestModal.styles';

interface FormState {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  userRoles: Record<string, boolean>;
  helperTexts: Record<string, string>;
}

interface ApplicationRequestModalProps {
  isOpenApplicationRequestModal: boolean;
  onCloseApplicationRequestModal: () => void;
}

const ClEAR_PHONE_NUMBER_LENGTH = 10;

export const ApplicationRequestModal = (props: ApplicationRequestModalProps) => {
  const { isOpenApplicationRequestModal, onCloseApplicationRequestModal } = props;

  const navigate = useNavigate();

  const { updateRecommenderInfo, isLoading, userRoles, currentUserRoles } = useCommonAppAdminStore((store) => store);
  const { firstName, lastName, displayName, phone, email, organizationName } = useUserProfile();

  const initialFormState: FormState = {
    firstName: firstName || '',
    lastName: lastName || '',
    phone: phone || '',
    email: email || '',
    userRoles: {
      [Roles.TEACHER]: false,
      [Roles.COUNSELOR]: false,
    },
    helperTexts: {
      email: '',
      phone: '',
    },
  };

  const [formState, setFormState] = useState<FormState>(initialFormState);

  useEffect(() => {
    if (firstName || lastName || phone || email) {
      updateFormValues({
        firstName: firstName || '',
        lastName: lastName || '',
        phone: phone || '',
        email: email || '',
      });
    }
  }, [firstName, lastName, phone, email]);

  const isValidPhoneNumber = useMemo(() => {
    return (
      formState.phone.replaceAll(' ', '').replaceAll('-', '').replaceAll('+1', '').length >= ClEAR_PHONE_NUMBER_LENGTH
    );
  }, [formState]);

  const isConfirmDisabled =
    !formState.firstName ||
    !formState.lastName ||
    !isValidPhoneNumber ||
    !validateEmail(formState.email) ||
    !Object.values(formState.userRoles).some((roleStatus) => roleStatus) ||
    isLoading;

  const updateFormValues = (fieldUpdates: Partial<FormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...fieldUpdates,
    }));
  };

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateFormValues({ [event.target.name]: event.target.value });
  };

  const handleChangeEmail = (event: ChangeEvent<HTMLInputElement>) => {
    if (validateEmail(event.target.value)) {
      updateFormValues({ helperTexts: { email: initialFormState.helperTexts.email } });
    } else {
      updateFormValues({ helperTexts: { email: HELPER_TEXT.emailValidation } });
    }

    updateFormValues({ email: event.target.value });
  };

  const handleCheckBoxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    updateFormValues({
      userRoles: {
        ...formState?.userRoles,
        [event.target.name]: checked,
      },
    });
  };

  const handleSaveForm = async () => {
    const roles = Object.keys(formState?.userRoles).reduce((acc, role) => {
      if (formState?.userRoles[role]) {
        return [...acc, role];
      }

      return acc;
    }, [] as string[]);

    const preparedSaveData: RecommenderInfo = {
      fullName: `${formState.firstName} ${formState.lastName}`,
      displayName: displayName || '',
      email: formState.email,
      phone: formState.phone.replaceAll(' ', '').replaceAll('-', ''),
      roles: roles as RoleEnum[],
    };

    await updateRecommenderInfo(preparedSaveData, true);

    onCloseApplicationRequestModal();
  };

  const handleCancelForm = () => {
    onCloseApplicationRequestModal();

    if (userRoles?.length === 0) {
      navigate(ECP_APPS_URL);
    }
  };

  useEffect(() => {
    updateFormValues({
      userRoles: {
        [Roles.TEACHER]: !!userRoles?.includes(Roles.TEACHER),
        [Roles.COUNSELOR]: !!userRoles?.includes(Roles.COUNSELOR),
      },
    });
  }, [userRoles]);

  return (
    <CoreDialog sx={sxStyles.dialog} open={isOpenApplicationRequestModal}>
      <StyledDialogTitle>Verify your information</StyledDialogTitle>

      <CoreDialogContent>
        <CoreBox sx={sxStyles.contentInner}>
          <CoreTypography sx={sxStyles.contentTitle} variant="h4" component="h3">
            Accessing Your Common App Applicant Dashboard
          </CoreTypography>

          <CoreTypography sx={sxStyles.description}>Please confirm your personal details and role.</CoreTypography>

          <CoreBox sx={sxStyles.fieldsWrap}>
            <CoreBox sx={sxStyles.fullName}>
              <CoreFormControl fullWidth>
                <TextField
                  name="firstName"
                  label="First name"
                  value={formState.firstName}
                  required
                  onChange={handleTextFieldChange}
                />
              </CoreFormControl>

              <CoreFormControl fullWidth>
                <TextField
                  name="lastName"
                  label="Last name"
                  value={formState.lastName}
                  required
                  onChange={handleTextFieldChange}
                />
              </CoreFormControl>
            </CoreBox>

            <InputMask name="phone" label="Phone" value={formState.phone} isRequired onChange={handleTextFieldChange} />

            <CoreFormControl fullWidth>
              <TextField
                name="email"
                label="Email"
                value={formState.email}
                required
                error={!!formState.helperTexts.email}
                helperText={formState.helperTexts.email}
                onChange={handleChangeEmail}
              />
            </CoreFormControl>

            <CoreBox>
              <CoreTypography
                variant="h6"
                component="h5"
              >{`Please specify your role at ${organizationName} *`}</CoreTypography>

              <CoreFormGroup sx={sxStyles.checkboxGroup} row>
                <CoreFormControlLabel
                  control={
                    <CoreCheckbox
                      name={Roles.TEACHER}
                      checked={formState.userRoles.Teacher}
                      onChange={handleCheckBoxChange}
                    />
                  }
                  label={Roles.TEACHER}
                  disabled={currentUserRoles?.isAssignedTeacher}
                />

                <CoreFormControlLabel
                  control={
                    <CoreCheckbox
                      name={Roles.COUNSELOR}
                      checked={formState.userRoles.Counselor}
                      onChange={handleCheckBoxChange}
                    />
                  }
                  label={Roles.COUNSELOR}
                  disabled={currentUserRoles?.isAssignedCounselor}
                />

                {currentUserRoles?.isAssignedTeacher || currentUserRoles?.isAssignedCounselor ? (
                  <CoreFormHelperText>
                    It seems there are already students waiting for your recommendations and reports for the application
                    process.
                  </CoreFormHelperText>
                ) : null}

                {!Object.values(formState.userRoles).some((roleStatus) => roleStatus) ? (
                  <CoreFormHelperText>Please select your role.</CoreFormHelperText>
                ) : null}
              </CoreFormGroup>
            </CoreBox>
          </CoreBox>
        </CoreBox>
      </CoreDialogContent>

      <DialogFooter
        goBackButtonTitle="Cancel"
        goNextButtonTitle="Confirm"
        goNextActionButtonTitle="Saving..."
        onGoBack={handleCancelForm}
        onGoNext={handleSaveForm}
        isDisableNextButton={isConfirmDisabled}
      />
    </CoreDialog>
  );
};
